import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLazyGetUserRolesQuery } from '@/store/api';
import { 
  IUserManagementListResponse, 
  IUserManagementTableRow,
  IFilterModel,
  ISortModel,
  IPaginationModel,
  IApiUser,
  IUseUserManagement
} from '@/types/admin/userManagement';
import { useAdminUser } from './useAdminUser';
import { handleApiError, EnhancedError } from '@/utils/admin/errorHandling';
import { constructUserOrgs, constructUserProperties } from '@/utils/admin/userManagementUtils';
import { ROWS_PER_PAGE } from '@/constants/admin';
import { useDataTableState } from '@/contexts/admin/DataTableContext';
import { applyTextFilter, applyArrayFilter, compareValues } from '@/utils/admin/filterUtils';

/**
 * Normalize table row data (exact copy of admin-web logic)
 */
const normalizeTableRow = (user: IApiUser): IUserManagementTableRow => {
  return {
    id: user.id,
    name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'No Details Found',
    userType: user.roles?.length ? user.roles[0].name : '',
    email: user.email,
    organization: constructUserOrgs(user),
    properties: constructUserProperties(user),
  };
};

export const useUserManagement = (): IUseUserManagement => {
  const adminUser = useAdminUser();
  
  // Table UI state from global table slice keyed to this screen
  const table = useDataTableState('admin:userManagement');
  const filterModel = table.filterModel;
  const sortModel = table.sortModel;
  const paginationModel = table.paginationModel;
  const searchQuery = table.searchQuery;

  // Error handling state
  const [enhancedError, setEnhancedError] = useState<EnhancedError | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);

  // RTK Query hook (lazy query for manual triggering like admin-web)
  const [getUserRoles, { 
    data, 
    isLoading, 
    isFetching, 
    isUninitialized, 
    error 
  }] = useLazyGetUserRolesQuery();

  /**
   * Enhanced trigger fetch with error handling
   * NOTE: User management API fetches ALL users at once (no server-side pagination like resident list)
   */
  const triggerFetch = useCallback(async () => {
    // Clear previous error
    setEnhancedError(null);

    try {
      const result = await getUserRoles(undefined).unwrap();
      
      // Reset retry count on success
      setRetryCount(0);
    } catch (fetchError) {
      // Handle and log the error
      const errorDetails = handleApiError(fetchError, '/api/v1/roles/list', retryCount);
      setEnhancedError(errorDetails);
      setRetryCount(prev => prev + 1);
      
      console.error('🔴 Failed to fetch user management list after retries:', errorDetails);
    }
  }, [
    getUserRoles,
    retryCount
  ]);

  /**
   * Safe data access with default values
   */
  const userManagementData: IUserManagementListResponse = useMemo(() => {
    if (!data || !data.response) {
      return { success: false, response: [] };
    }
    return { success: data.success, response: data.response };
  }, [data]);

  /**
   * Normalized table rows for display (all users from API)
   */
  const allTableRows = useMemo(() => {
    return userManagementData?.response?.map(normalizeTableRow) || [];
  }, [userManagementData]);

  /**
   * Client-side filtered and sorted table rows (matching admin-web behavior)
   */
  const filteredAndSortedRows = useMemo(() => {
    let rows = [...allTableRows];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      rows = rows.filter(row => 
        row.name.toLowerCase().includes(query) ||
        row.email.toLowerCase().includes(query)
      );
    }

    // Apply other filters using shared utilities
    filterModel.items.forEach(filter => {
      if (!filter.value) return;
      
      switch (filter.field) {
        case 'userType':
          rows = rows.filter(row => applyTextFilter(row.userType, filter.value, filter.operator));
          break;
          
        case 'email':
          rows = rows.filter(row => applyTextFilter(row.email, filter.value, filter.operator));
          break;
          
        case 'organization':
          rows = rows.filter(row => applyArrayFilter(row.organization, filter.value, filter.operator));
          break;
          
        case 'properties':
          rows = rows.filter(row => applyArrayFilter(row.properties, filter.value, filter.operator));
          break;
      }
    });

    // Apply sorting using shared utility
    if (sortModel.length > 0) {
      const sort = sortModel[0];
      rows.sort((a, b) => {
        const aVal = a[sort.field as keyof IUserManagementTableRow];
        const bVal = b[sort.field as keyof IUserManagementTableRow];
        return compareValues(aVal, bVal, sort.sort);
      });
    }

    return rows;
  }, [allTableRows, searchQuery, filterModel, sortModel]);

  /**
   * Client-side paginated table rows (matching admin-web MUI DataGrid behavior)
   */
  const tableRows = useMemo(() => {
    const startIndex = paginationModel.page * paginationModel.pageSize;
    const endIndex = startIndex + paginationModel.pageSize;
    return filteredAndSortedRows.slice(startIndex, endIndex);
  }, [filteredAndSortedRows, paginationModel]);

  /**
   * Loading state computation - only show loading on initial load or when no data exists
   */
  const isTableLoading = (isLoading || isUninitialized) && (userManagementData.response.length === 0);

  /**
   * Initial fetch trigger (only fetch once since we get all users)
   */
  useEffect(() => {
    triggerFetch();
  }, [triggerFetch]);

  /**
   * Reset pagination when filters or sort change (but don't trigger fetch here)
   */
  useEffect(() => {
    if (paginationModel.page !== 0) {
      table.setPagination({ ...paginationModel, page: 0 });
    }
  }, [filterModel, sortModel]);

  /**
   * Reset pagination when search changes (separate effect for debounced search)
   */
  useEffect(() => {
    if (paginationModel.page !== 0) {
      table.setPagination({ ...paginationModel, page: 0 });
    }
  }, [searchQuery]);

  /**
   * Update filter model
   */
  const updateFilterModel = useCallback((newFilterModel: IFilterModel) => {
    table.setFilter(newFilterModel);
  }, [table]);

  /**
   * Update sort model
   */
  const updateSortModel = useCallback((newSortModel: ISortModel[]) => {
    table.setSort(newSortModel);
  }, [table]);

  /**
   * Update pagination
   */
  const updatePaginationModel = useCallback((newPaginationModel: IPaginationModel) => {
    table.setPagination(newPaginationModel);
  }, [table]);

  /**
   * Update search query
   */
  const updateSearchQuery = useCallback((query: string) => {
    table.setSearch(query);
  }, [table]);

  /**
   * Refresh data manually
   */
  const refresh = useCallback(() => {
    triggerFetch();
  }, [triggerFetch]);

  /**
   * Go to next page (based on filtered results, not total API results)
   */
  const goToNextPage = useCallback(() => {
    const maxPage = Math.ceil(filteredAndSortedRows.length / paginationModel.pageSize) - 1;
    if (paginationModel.page < maxPage) {
      table.setPagination({ ...paginationModel, page: paginationModel.page + 1 });
    }
  }, [filteredAndSortedRows.length, paginationModel, table]);

  /**
   * Go to previous page
   */
  const goToPreviousPage = useCallback(() => {
    if (paginationModel.page > 0) {
      table.setPagination({ ...paginationModel, page: paginationModel.page - 1 });
    }
  }, [paginationModel.page, paginationModel, table]);

  /**
   * Check if there's a next page (based on filtered results)
   */
  const hasNextPage = useMemo(() => {
    const maxPage = Math.ceil(filteredAndSortedRows.length / paginationModel.pageSize) - 1;
    return paginationModel.page < maxPage;
  }, [filteredAndSortedRows.length, paginationModel]);

  /**
   * Check if there's a previous page
   */
  const hasPreviousPage = useMemo(() => {
    return paginationModel.page > 0;
  }, [paginationModel.page]);

  /**
   * Manual retry function for user-initiated retries
   */
  const manualRetry = useCallback(() => {
    setEnhancedError(null);
    triggerFetch();
  }, [triggerFetch]);

  return {
    // Data (provide correct structure for DataTable component)
    data: {
      success: userManagementData.success,
      response: userManagementData.response, // Keep original API response structure
    },
    tableRows,
    
    // Additional data for proper pagination display
    totalFilteredCount: filteredAndSortedRows.length,
    
    // State
    isLoading: isTableLoading,
    error: error ? String(error) : null,
    
    // Enhanced Error Handling
    enhancedError,
    retryCount,
    canRetry: enhancedError?.shouldRetry || false,
    manualRetry,
    
    // Pagination
    paginationModel,
    updatePaginationModel,
    goToNextPage,
    goToPreviousPage,
    hasNextPage,
    hasPreviousPage,
    
    // Filtering & Sorting
    filterModel,
    updateFilterModel,
    sortModel,
    updateSortModel,
    searchQuery,
    updateSearchQuery,
    
    // Actions
    refresh,
    getUserList: triggerFetch,
  };
};
